/**
 * MobileScheduleView Component
 * Mobile-optimized day-by-day schedule view
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Card,
  CardContent,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EmailIcon from '@mui/icons-material/Email';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { DAYS } from '../constants';
import { formatShiftDisplay } from '../utils/timeHelpers';

/**
 * MobileScheduleView - Day-by-day mobile view
 */
export default function MobileScheduleView({
  // Data
  selectedWeek,
  shifts,
  positions,
  dayData,
  currentDay,
  dayDate,
  selectedDayIndex,
  searchTerm,
  selectedPosition,
  generatingFixed,
  // Handlers
  onWeekChange,
  onDayChange,
  onSearchChange,
  onPositionChange,
  onOpenCopyWeek,
  onGenerateFixed,
  onNotify,
  onOpenEmployee,
  onQuickAdd,
  onDeleteShift,
  getEmployeeDisplayPosition,
}) {
  return (
    <Box sx={{ pb: 10 }}>
      {/* Header with Week Picker */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Πρόγραμμα
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={() => onWeekChange(-1)}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="body2" sx={{ alignSelf: 'center', fontWeight: 500 }}>
              {selectedWeek.format('DD/MM')} - {selectedWeek.add(6, 'day').format('DD/MM')}
            </Typography>
            <IconButton size="small" onClick={() => onWeekChange(1)}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={onOpenCopyWeek}
            disabled={shifts.length === 0}
          >
            Αντιγραφή
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="secondary"
            startIcon={<AutorenewIcon />}
            onClick={onGenerateFixed}
            disabled={generatingFixed}
          >
            {generatingFixed ? '...' : 'Σταθερά'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="info"
            startIcon={<EmailIcon />}
            onClick={onNotify}
            disabled={shifts.length === 0}
          >
            Email
          </Button>
        </Box>
      </Paper>

      {/* Day Tabs */}
      <Paper sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs
          value={selectedDayIndex}
          onChange={(e, newValue) => onDayChange(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 2,
              py: 1.5,
              fontSize: '0.85rem',
              fontWeight: 600,
            },
            '& .Mui-selected': {
              bgcolor: 'primary.main',
              color: '#fff !important',
              borderRadius: 1,
            },
          }}
        >
          {DAYS.map((day, index) => {
            const dayOffset = day.value === 0 ? 6 : day.value - 1;
            const date = selectedWeek.add(dayOffset, 'day');
            const shiftsCount = shifts.filter(s => String(s.day) === String(day.value)).length;
            
            return (
              <Tab
                key={day.value}
                label={
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {day.label}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                      {date.format('DD/MM')}
                    </Typography>
                    {shiftsCount > 0 && (
                      <Chip
                        label={shiftsCount}
                        size="small"
                        sx={{ 
                          ml: 0.5, 
                          height: 18, 
                          fontSize: '0.7rem',
                          bgcolor: 'success.light',
                          color: 'success.contrastText'
                        }}
                      />
                    )}
                  </Box>
                }
              />
            );
          })}
        </Tabs>
      </Paper>

      {/* Day Header */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {currentDay.fullLabel}, {dayDate.format('DD/MM/YYYY')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {dayData.filter(d => d.shift).length} βάρδιες
        </Typography>
      </Box>

      {/* Search & Filter */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            label="Αναζήτηση"
            placeholder="Όνομα..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Πόστο</InputLabel>
            <Select
              value={selectedPosition}
              label="Πόστο"
              onChange={(e) => onPositionChange(e.target.value)}
            >
              <MenuItem value=""><em>Όλα</em></MenuItem>
              {positions.map((position) => (
                <MenuItem key={position} value={position}>{position}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Employee Cards - Compact Layout */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {dayData.map(({ employee, shift }) => (
          <Card
            key={employee.id}
            sx={{
              borderRadius: 2,
              border: shift ? '2px solid' : '1px solid',
              borderColor: shift ? 'success.main' : 'divider',
              bgcolor: shift ? 'success.50' : 'background.paper',
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              {/* Three column layout: Name | Time (center) | Buttons */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1
              }}>
                {/* Left: Name + Position */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'primary.main',
                      cursor: 'pointer',
                      '&:hover': { textDecoration: 'underline' },
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    onClick={() => onOpenEmployee(employee)}
                  >
                    {employee.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {(getEmployeeDisplayPosition ? getEmployeeDisplayPosition(employee) : employee.position) || 'Χωρίς πόστο'}
                  </Typography>
                </Box>

                {/* Center: Time (fixed width for alignment) */}
                <Box sx={{ width: 105, textAlign: 'center', flexShrink: 0 }}>
                  {shift && (
                    <Chip
                      size="small"
                      label={formatShiftDisplay(shift)}
                      color="success"
                      sx={{ 
                        fontWeight: 600, 
                        fontSize: '0.75rem',
                        height: 24,
                        '& .MuiChip-label': { px: 1 }
                      }}
                    />
                  )}
                </Box>

                {/* Right: Action Buttons */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  {shift ? (
                    <>
                      <IconButton
                        size="small"
                        onClick={() => onQuickAdd(employee)}
                        sx={{ 
                          bgcolor: 'primary.main',
                          color: 'white',
                          width: 36,
                          height: 36,
                          '&:hover': { bgcolor: 'primary.dark' }
                        }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteShift(shift.id, employee.name)}
                        sx={{ 
                          bgcolor: 'error.main',
                          color: 'white',
                          width: 36,
                          height: 36,
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </>
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => onQuickAdd(employee)}
                      sx={{ 
                        bgcolor: 'primary.main',
                        color: 'white',
                        width: 36,
                        height: 36,
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      <AddIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {dayData.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography color="text.secondary">
            Δεν βρέθηκαν υπάλληλοι
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

